import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_BASE_URL, getHeaders } from "../utils/api";
import { readAdminApiErrorMessage } from "../utils/adminUi";
import { ApiEndpoints } from "../utils/ApiEndpoints";
import SearchableDropdown from "./shared/SearchableDropdown";
import { showToast } from "../utils/toast";
import { trackEvent } from "../services/applicationInsights";
import {
  ORG_STORAGE_KEY,
  getInitialOrgId,
  persistOrgSelection,
  validateAndReconcileSelection
} from "./utils/adminScopeStorage";
import {
  FaPuzzlePiece,
  FaTools,
  FaCheck,
  FaTimes,
  FaExclamationTriangle,
  FaSyncAlt,
  FaShieldAlt,
  FaInfoCircle,
  FaCopy,
  FaGlobe,
  FaDownload,
  FaPlus
} from "react-icons/fa";

/* ------------------ Main Component ------------------ */
const EnrollmentKeys = ({ token, orgId, roles = [], onNavigate }) => {
  const isSystemAdmin = useMemo(() => {
    const roleList = Array.isArray(roles) ? roles : [];
    return roleList.some((role) => String(role).toLowerCase() === "systemadministrator");
  }, [roles]);

  const fetchKeysAbortRef = useRef(null);
  const fetchOriginsAbortRef = useRef(null);
  const fetchDeploymentAbortRef = useRef(null);

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(() => getInitialOrgId(isSystemAdmin, orgId));
  const [fetchingOrgs, setFetchingOrgs] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Enrollment key modal states
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [labelInput, setLabelInput] = useState("");
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [showRotateModal, setShowRotateModal] = useState(false);
  const [rotateTarget, setRotateTarget] = useState(null);
  const [revealedKey, setRevealedKey] = useState(null); // { id, enrollmentKey, createdAt }
  const [revealCopied, setRevealCopied] = useState(false);

  // Enrollment key list
  const [enrollmentKeys, setEnrollmentKeys] = useState([]);
  const [fetchingKeys, setFetchingKeys] = useState(false);

  // Origins
  const [origins, setOrigins] = useState([]);
  const [fetchingOrigins, setFetchingOrigins] = useState(false);
  const [originsError, setOriginsError] = useState('');

  // Deployment info / download
  const [deploymentInfo, setDeploymentInfo] = useState(null);
  const [fetchingDeployment, setFetchingDeployment] = useState(false);

  useEffect(() => {
    return () => {
      fetchKeysAbortRef.current?.abort();
      fetchOriginsAbortRef.current?.abort();
      fetchDeploymentAbortRef.current?.abort();
    };
  }, []);

  /* ------------------ Error parsers ------------------ */
  const parseOrganizationsError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      401: "Your session is invalid or expired. Please sign in again.",
      403: "You do not have permission to view organizations.",
      500: "An error occurred while loading organizations.",
      default: "Failed to load organizations"
    });
  }, []);

  const parseKeyListError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      401: "Your session is invalid or expired. Please sign in again.",
      403: "You do not have permission to view enrollment keys for this organization.",
      500: "An error occurred while retrieving enrollment keys.",
      default: "Failed to fetch enrollment keys"
    });
  }, []);

  const parseGenerateKeyError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      400: "Invalid request to generate an enrollment key.",
      401: "Your session is invalid or expired. Please sign in again.",
      403: "You do not have permission to generate enrollment keys for this organization.",
      409: "Maximum allowed enrollment keys reached. Revoke an existing key before generating a new one.",
      500: "An unexpected server error occurred while generating the enrollment key.",
      default: "Failed to generate enrollment key"
    });
  }, []);

  const parseRotateKeyError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      401: "Your session is invalid or expired. Please sign in again.",
      403: "You do not have permission to rotate this enrollment key.",
      404: "Enrollment key not found.",
      500: "An unexpected server error occurred while rotating the key.",
      default: "Failed to rotate enrollment key"
    });
  }, []);

  const parseRevokeKeyError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      401: "Your session is invalid or expired. Please sign in again.",
      403: "You do not have permission to revoke this enrollment key.",
      404: "Enrollment key not found.",
      500: "An unexpected server error occurred while revoking the key.",
      default: "Failed to revoke enrollment key"
    });
  }, []);

  const parseOriginListError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      401: "Your session is invalid or expired. Please sign in again.",
      403: "You do not have permission to view origins for this organization.",
      500: "An error occurred while retrieving origins.",
      default: "Failed to fetch origins"
    });
  }, []);

  const parseDeploymentInfoError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      401: "Your session is invalid or expired. Please sign in again.",
      403: "You do not have permission to view deployment info for this organization.",
      500: "An error occurred while retrieving deployment info.",
      default: "Failed to fetch deployment info"
    });
  }, []);

  /* ------------------ Org resolution ------------------ */
  useEffect(() => {
    if (!isSystemAdmin && orgId && selectedOrg !== orgId) {
      setSelectedOrg(orgId);
    }
  }, [isSystemAdmin, orgId, selectedOrg]);

  useEffect(() => {
    if (!isSystemAdmin) return;

    setFetchingOrgs(true);
    fetch(`${API_BASE_URL}/organizations`, { headers: getHeaders(token) })
      .then(async (res) => {
        if (!res.ok) {
          const message = await parseOrganizationsError(res);
          throw new Error(message);
        }
        return res.json().catch(() => []);
      })
      .then((data) => {
        const organizationsList = Array.isArray(data) ? data : [];
        setOrganizations(organizationsList);
        setSelectedOrg((prev) => validateAndReconcileSelection(organizationsList, prev, isSystemAdmin ? ORG_STORAGE_KEY : null));
      })
      .catch((err) => showToast.error(err.message || "Failed to load organizations"))
      .finally(() => setFetchingOrgs(false));
  }, [isSystemAdmin, parseOrganizationsError, token]);

  const effectiveOrgId = isSystemAdmin ? selectedOrg : selectedOrg || orgId;

  /* ------------------ Fetch: enrollment keys ------------------ */
  const fetchEnrollmentKeys = useCallback(async () => {
    if (!effectiveOrgId) return;

    fetchKeysAbortRef.current?.abort();
    const controller = new AbortController();
    fetchKeysAbortRef.current = controller;

    setFetchingKeys(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/organizations/${effectiveOrgId}/extension/enrollment-keys`,
        { headers: getHeaders(token), signal: controller.signal }
      );

      if (res.status === 404) {
        setEnrollmentKeys([]);
        return;
      }
      if (!res.ok) {
        const message = await parseKeyListError(res);
        throw new Error(message);
      }

      const data = await res.json();
      setEnrollmentKeys(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setEnrollmentKeys([]);
      showToast.error(e.message);
    } finally {
      if (fetchKeysAbortRef.current === controller) setFetchingKeys(false);
    }
  }, [effectiveOrgId, parseKeyListError, token]);

  /* ------------------ Fetch: origins ------------------ */
  const fetchOrigins = useCallback(async () => {
    if (!effectiveOrgId) return;

    fetchOriginsAbortRef.current?.abort();
    const controller = new AbortController();
    fetchOriginsAbortRef.current = controller;

    setFetchingOrigins(true);
    setOriginsError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}${ApiEndpoints.extensionOrigins(effectiveOrgId)}`,
        { headers: getHeaders(token), signal: controller.signal }
      );

      if (res.status === 404) {
        setOrigins([]);
        return;
      }
      if (!res.ok) {
        const message = await parseOriginListError(res);
        throw new Error(message);
      }

      const data = await res.json();
      setOrigins(Array.isArray(data) ? data.filter((o) => o.isActive !== false) : []);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setOrigins([]);
      setOriginsError(e.message || 'Origin status is unavailable.');
      showToast.error(e.message);
    } finally {
      if (fetchOriginsAbortRef.current === controller) setFetchingOrigins(false);
    }
  }, [effectiveOrgId, parseOriginListError, token]);

  /* ------------------ Fetch: deployment info ------------------ */
  const fetchDeploymentInfo = useCallback(async () => {
    if (!effectiveOrgId) return;

    fetchDeploymentAbortRef.current?.abort();
    const controller = new AbortController();
    fetchDeploymentAbortRef.current = controller;

    setFetchingDeployment(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/organizations/${effectiveOrgId}/extension/deployment-info`,
        { headers: getHeaders(token), signal: controller.signal }
      );

      if (!res.ok) {
        const message = await parseDeploymentInfoError(res);
        throw new Error(message);
      }

      const data = await res.json().catch(() => null);
      setDeploymentInfo(data);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setDeploymentInfo(null);
      showToast.error(e.message);
    } finally {
      if (fetchDeploymentAbortRef.current === controller) setFetchingDeployment(false);
    }
  }, [effectiveOrgId, parseDeploymentInfoError, token]);

  useEffect(() => {
    if (!effectiveOrgId) {
      fetchKeysAbortRef.current?.abort();
      fetchOriginsAbortRef.current?.abort();
      fetchDeploymentAbortRef.current?.abort();
      setEnrollmentKeys([]);
      setOrigins([]);
      setDeploymentInfo(null);
      return;
    }

    Promise.all([fetchEnrollmentKeys(), fetchOrigins(), fetchDeploymentInfo()]);
  }, [effectiveOrgId, fetchEnrollmentKeys, fetchOrigins, fetchDeploymentInfo]);

  /* ------------------ Formatting helpers ------------------ */
  const formatDate = (value) => {
    if (!value) return "—";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString();
    } catch {
      return String(value);
    }
  };

  /* ------------------ Enrollment key actions ------------------ */
  const handleGenerateClick = () => {
    if (!effectiveOrgId) {
      showToast.warning("Please select an organization first.");
      return;
    }
    setLabelInput("");
    setShowGenerateModal(true);
  };

  const handleGenerateConfirm = async () => {
    if (!effectiveOrgId) return;
    if (!labelInput.trim()) {
      showToast.warning("Please enter a label for this key.");
      return;
    }

    setSubmitting(true);
    setShowGenerateModal(false);

    try {
      const res = await fetch(
        `${API_BASE_URL}/organizations/${effectiveOrgId}/extension/enrollment-keys`,
        {
          method: "POST",
          headers: getHeaders(token),
          body: JSON.stringify({ label: labelInput.trim() })
        }
      );

      if (!res.ok) {
        if (res.status === 409) {
          throw new Error(
            "Maximum allowed enrollment keys reached. Revoke an existing key before generating a new one."
          );
        }
        const message = await parseGenerateKeyError(res);
        throw new Error(message);
      }

      const created = await res.json().catch(() => null);
      if (created?.enrollmentKey) {
        setRevealedKey({
          id: created.id,
          enrollmentKey: created.enrollmentKey,
          createdAt: created.createdAt
        });
        setRevealCopied(false);
      }

      trackEvent("Enrollment Key Generated", { organizationId: effectiveOrgId });
      showToast.success("Enrollment key generated successfully.");
      await fetchEnrollmentKeys();
    } catch (e) {
      showToast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRotateClick = (keyItem) => {
    if (!keyItem?.id) {
      showToast.error("Cannot rotate: key identifier is missing.");
      return;
    }
    setRotateTarget(keyItem);
    setShowRotateModal(true);
  };

  const handleRotateConfirm = async () => {
    const keyId = rotateTarget?.id;
    if (!effectiveOrgId || !keyId) return;

    setSubmitting(true);
    setShowRotateModal(false);

    try {
      const res = await fetch(
        `${API_BASE_URL}/organizations/${effectiveOrgId}/extension/enrollment-keys/${keyId}/rotate`,
        { method: "POST", headers: getHeaders(token) }
      );

      if (!res.ok) {
        const message = await parseRotateKeyError(res);
        throw new Error(message);
      }

      const rotated = await res.json().catch(() => null);
      if (rotated?.enrollmentKey) {
        setRevealedKey({
          id: rotated.id,
          enrollmentKey: rotated.enrollmentKey,
          createdAt: rotated.createdAt
        });
        setRevealCopied(false);
      }

      trackEvent("Enrollment Key Rotated", { organizationId: effectiveOrgId, keyId });
      showToast.success("Enrollment key rotated successfully. The old key is now invalid.");
      setRotateTarget(null);
      await fetchEnrollmentKeys();
    } catch (e) {
      showToast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevokeClick = (keyItem) => {
    if (!keyItem?.id) {
      showToast.error("Cannot revoke: key identifier is missing.");
      return;
    }
    setRevokeTarget(keyItem);
    setShowRevokeModal(true);
  };

  const handleRevokeConfirm = async () => {
    const keyId = revokeTarget?.id;
    if (!effectiveOrgId || !keyId) return;

    setSubmitting(true);
    setShowRevokeModal(false);

    try {
      const res = await fetch(
        `${API_BASE_URL}/organizations/${effectiveOrgId}/extension/enrollment-keys/${keyId}`,
        { method: "DELETE", headers: getHeaders(token) }
      );

      if (!res.ok) {
        if (res.status === 404) throw new Error("Enrollment key not found.");
        const message = await parseRevokeKeyError(res);
        throw new Error(message);
      }

      trackEvent("Enrollment Key Revoked", { organizationId: effectiveOrgId, keyId });
      showToast.success("Enrollment key revoked successfully.");
      setRevokeTarget(null);
      await fetchEnrollmentKeys();
    } catch (e) {
      showToast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  /* ------------------ Clipboard ------------------ */
  const copyToClipboard = async (value) => {
    if (!value) return false;
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        return true;
      } catch {
        return false;
      }
    }
  };

  const copyRevealedKey = async () => {
    const ok = await copyToClipboard(revealedKey?.enrollmentKey);
    if (ok) {
      setRevealCopied(true);
      showToast.success("Enrollment key copied to clipboard.");
    } else {
      showToast.error("Copy failed. Please copy manually.");
    }
  };

  const closeRevealModal = () => {
    setRevealedKey(null);
    setRevealCopied(false);
  };

  /* ------------------ Download availability ------------------ */
  const availableBrowsers = Array.isArray(deploymentInfo?.browsers) ? deploymentInfo.browsers : [];
  const hasDownloads = availableBrowsers.length > 0;

  const handleDownloadClick = (browserEntry) => {
    const url = browserEntry?.downloadUrl || browserEntry?.url;
    if (!url) {
      showToast.error("Download link is not available for this browser yet.");
      return;
    }
    trackEvent("Extension Downloaded", { organizationId: effectiveOrgId, browser: browserEntry?.browser || browserEntry?.name });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div style={wrapperStyle}>
      <div style={containerStyle}>
        <header style={headerStyle}>
          <div style={headerContentStyle}>
            <div style={headerBadgeStyle}>
              <FaPuzzlePiece size={22} />
            </div>
            <div>
              <h1 style={titleStyle}>Extension Player Setup</h1>
              <p style={subtitleStyle}>
                Generate enrollment keys, review origin status, and download the browser extension.
              </p>
            </div>
          </div>
        </header>

        {/* Org selector */}
        {isSystemAdmin && (
          <div style={filtersCardStyle}>
            <div style={cardHeaderStyle}>
              <FaTools color="#6366f1" size={14} />
              <span style={cardTitleStyle}>Extension Player Config</span>
            </div>

            <div style={filtersGridStyleSingleColumn}>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Organization</label>
                <SearchableDropdown
                  options={organizations}
                  selectedValue={selectedOrg}
                  onSelect={(val) => {
                    setSelectedOrg(val);
                    persistOrgSelection(isSystemAdmin, val);
                    if (val) {
                      trackEvent("Dropdown Selected", { section: "enrollmentKeys", organizationId: val });
                    }
                  }}
                  placeholder="Select Organization"
                  isLoading={fetchingOrgs}
                  disabled={fetchingOrgs}
                  styles={searchableDropdownStyles}
                />
              </div>
            </div>
          </div>
        )}

        {/* Enrollment key actions */}
        <div style={actionCardStyle}>
          <div style={cardHeaderStyle}>
            <FaShieldAlt color="#6366f1" size={14} />
            <span style={cardTitleStyle}>Enrollment Key Actions</span>
          </div>
          <div style={actionButtonsRow}>
            <button
              onClick={handleGenerateClick}
              disabled={submitting || !effectiveOrgId}
              style={{
                ...primaryButtonStyle,
                opacity: submitting || !effectiveOrgId ? 0.6 : 1
              }}
            >
              <FaPlus /> Generate Enrollment Key
            </button>

            <button
              onClick={() => fetchEnrollmentKeys()}
              disabled={submitting || !effectiveOrgId}
              style={{
                ...secondaryButtonStyle,
                opacity: submitting || !effectiveOrgId ? 0.6 : 1
              }}
            >
              <FaSyncAlt className={fetchingKeys ? "spin" : ""} /> Refresh
            </button>
          </div>
        </div>

        {/* Enrollment key list */}
        <div style={existingKeyCardStyle}>
          <div style={{ ...cardHeaderStyle, justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <FaShieldAlt color="#6366f1" size={14} />
              <span style={cardTitleStyle}>Enrollment Keys</span>
            </div>
            {fetchingKeys && (
              <div style={loadingTextStyle}>
                <FaSyncAlt className="spin" /> Loading...
              </div>
            )}
          </div>

          {fetchingKeys ? (
            <div style={emptyStateStyle}>
              <FaSyncAlt className="spin" /> Loading enrollment keys...
            </div>
          ) : enrollmentKeys.length === 0 ? (
            <div style={emptyStateStyle}>
              {isSystemAdmin && !selectedOrg
                ? "Select an organization to begin."
                : "No enrollment keys found. Generate one to get started."}
            </div>
          ) : (
            <div style={tableStyle}>
              <div style={{ ...tableRowStyle, ...tableHeaderRowStyle }}>
                <div style={tableCellStyle}>Label</div>
                <div style={tableCellStyle}>Created</div>
                <div style={tableCellStyle}>Last Used</div>
                <div style={tableCellStyle}>Status</div>
                <div style={{ ...tableCellStyle, textAlign: "right" }}>Actions</div>
              </div>

              {enrollmentKeys.map((k) => {
                const isActive = !k.isRevoked;
                return (
                  <div key={k.id} style={tableRowStyle}>
                    <div style={{ ...tableCellStyle, wordBreak: "break-word" }}>{k.label || "—"}</div>
                    <div style={tableCellStyle}>{formatDate(k.createdAt)}</div>
                    <div style={tableCellStyle}>{formatDate(k.lastUsedAt)}</div>
                    <div style={tableCellStyle}>
                      <span
                        style={{
                          ...statusBadgeStyle,
                          background: isActive ? "#d1fae5" : "#fee2e2",
                          color: isActive ? "#065f46" : "#991b1b"
                        }}
                      >
                        {isActive ? "Active" : "Revoked"}
                      </span>
                    </div>
                    <div style={{ ...tableCellStyle, textAlign: "right", display: "flex", gap: 8, justifyContent: "flex-end" }}>
                      <button
                        type="button"
                        onClick={() => handleRotateClick(k)}
                        disabled={submitting || !isActive}
                        style={{
                          ...secondaryActionButtonStyle,
                          opacity: submitting || !isActive ? 0.6 : 1,
                          cursor: submitting || !isActive ? "not-allowed" : "pointer"
                        }}
                        title={!isActive ? "Cannot rotate a revoked key" : "Rotate this key"}
                      >
                        Rotate
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRevokeClick(k)}
                        disabled={submitting || !isActive}
                        style={{
                          ...dangerButtonStyle,
                          opacity: submitting || !isActive ? 0.6 : 1,
                          cursor: submitting || !isActive ? "not-allowed" : "pointer"
                        }}
                        title={!isActive ? "Already revoked" : "Revoke this key"}
                      >
                        Revoke
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Origins */}
        <div style={filtersCardStyle}>
          <div style={cardHeaderStyle}>
            <FaGlobe color="#6366f1" size={14} />
            <span style={cardTitleStyle}>Enabled Origins</span>
          </div>

          <p>Origins are coordinated by site domain rendering and capture choices. Each distinct organization domain counts once, even when both capabilities are selected. Registry membership is not proof of a connected collector.</p>
          <button type="button" style={primaryButtonStyle}
            disabled={!effectiveOrgId || !onNavigate}
            onClick={() => onNavigate('sitecollections', { organizationId: effectiveOrgId })}>
            Manage domain choices in Site Collections
          </button>

          {fetchingOrigins ? (
            <div style={emptyStateStyle}>
              <FaSyncAlt className="spin" /> Loading origins...
            </div>
          ) : originsError ? (
            <p role="alert">Origin status unavailable: {originsError}</p>
          ) : origins.length === 0 ? (
            <div style={emptyStateStyle}>
              {isSystemAdmin && !selectedOrg
                ? "Select an organization to begin."
                : "No origins enabled yet."}
            </div>
          ) : (
            <div style={tableStyle}>
              <div style={{ ...originRowStyle, ...tableHeaderRowStyle }}>
                <div style={tableCellStyle}>Domain</div>
                <div style={tableCellStyle}>Enabled</div>
                <div style={{ ...tableCellStyle, textAlign: "right" }}>Status</div>
              </div>
              {origins.map((o) => (
                <div key={o.id} style={originRowStyle}>
                  <div style={{ ...tableCellStyle, fontFamily: "monospace" }}>{o.origin}</div>
                  <div style={tableCellStyle}>{formatDate(o.enabledAt)}</div>
                  <div style={{ ...tableCellStyle, textAlign: "right" }}>
                    Managed by site domain policy
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Download */}
        <div style={existingKeyCardStyle}>
          <div style={cardHeaderStyle}>
            <FaDownload color="#6366f1" size={14} />
            <span style={cardTitleStyle}>Download Extension</span>
          </div>

          {fetchingDeployment ? (
            <div style={emptyStateStyle}>
              <FaSyncAlt className="spin" /> Checking availability...
            </div>
          ) : !effectiveOrgId ? (
            <div style={emptyStateStyle}>Select an organization to see download options.</div>
          ) : hasDownloads ? (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {availableBrowsers.map((b, idx) => (
                <button
                  key={b.browser || b.name || idx}
                  onClick={() => handleDownloadClick(b)}
                  style={primaryButtonStyle}
                >
                  <FaDownload /> {b.browser || b.name || "Download"}
                </button>
              ))}
            </div>
          ) : (
            <div style={{ ...infoBoxStyle, background: "#fffbeb", borderColor: "#fde68a", color: "#92400e" }}>
              <FaInfoCircle color="#b45309" />
              <span style={infoTextStyle}>
                A packaged extension download isn't available for this organization yet. Once packaging and
                distribution are set up, download links will appear here automatically.
              </span>
            </div>
          )}
        </div>

        {/* Generate Modal */}
        {showGenerateModal && (
          <div style={modalOverlayStyle}>
            <div style={modalContentStyle}>
              <div style={modalHeaderStyle}>
                <FaShieldAlt color="#6366f1" size={20} />
                <h3 style={modalTitleStyle}>Generate Enrollment Key</h3>
              </div>
              <div style={modalBodyStyle}>
                <div style={infoBoxStyle}>
                  <FaInfoCircle color="#6366f1" />
                  <span style={infoTextStyle}>
                    A new enrollment key will be created for this organization. The full key value will be shown
                    <strong> only once</strong> after creation &mdash; copy it immediately.
                  </span>
                </div>
                <div style={inputGroupStyle}>
                  <label style={modalLabelStyle}>Label</label>
                  <input
                    type="text"
                    value={labelInput}
                    onChange={(e) => setLabelInput(e.target.value)}
                    placeholder="e.g. Sales team laptops"
                    style={modalInputStyle}
                    autoFocus
                  />
                </div>
              </div>
              <div style={modalFooterStyle}>
                <button onClick={() => setShowGenerateModal(false)} style={modalCancelButtonStyle}>
                  Cancel
                </button>
                <button onClick={handleGenerateConfirm} style={modalConfirmButtonStyle}>
                  <FaCheck /> Generate Key
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Rotate Confirmation Modal */}
        {showRotateModal && (
          <div style={modalOverlayStyle}>
            <div style={modalContentStyle}>
              <div style={modalHeaderStyle}>
                <FaExclamationTriangle color="#b45309" size={20} />
                <h3 style={modalTitleStyle}>Confirm Rotate</h3>
              </div>
              <div style={modalBodyStyle}>
                <div style={{ ...infoBoxStyle, background: "#fffbeb", borderColor: "#fde68a", color: "#92400e" }}>
                  <FaInfoCircle color="#b45309" />
                  <span style={infoTextStyle}>
                    Rotating replaces this key with a new value. The old key will stop working immediately —
                    any devices still using it will need the new key.
                  </span>
                </div>
                <div style={inputGroupStyle}>
                  <label style={modalLabelStyle}>Label</label>
                  <div style={{ ...modalInputStyle, background: "#f8fafc" }}>{rotateTarget?.label || ""}</div>
                </div>
              </div>
              <div style={modalFooterStyle}>
                <button
                  onClick={() => {
                    setShowRotateModal(false);
                    setRotateTarget(null);
                  }}
                  style={modalCancelButtonStyle}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRotateConfirm}
                  style={{ ...modalConfirmButtonStyle, background: "#b45309" }}
                >
                  <FaSyncAlt /> Rotate Key
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Revoke Confirmation Modal */}
        {showRevokeModal && (
          <div style={modalOverlayStyle}>
            <div style={modalContentStyle}>
              <div style={modalHeaderStyle}>
                <FaExclamationTriangle color="#b91c1c" size={20} />
                <h3 style={modalTitleStyle}>Confirm Revoke</h3>
              </div>
              <div style={modalBodyStyle}>
                <div style={{ ...infoBoxStyle, background: "#fef2f2", borderColor: "#fecaca", color: "#991b1b" }}>
                  <FaInfoCircle color="#991b1b" />
                  <span style={infoTextStyle}>
                    This action cannot be undone. Any device using this key will lose access immediately.
                  </span>
                </div>
                <div style={inputGroupStyle}>
                  <label style={modalLabelStyle}>Label</label>
                  <div style={{ ...modalInputStyle, background: "#f8fafc" }}>{revokeTarget?.label || ""}</div>
                </div>
              </div>
              <div style={modalFooterStyle}>
                <button
                  onClick={() => {
                    setShowRevokeModal(false);
                    setRevokeTarget(null);
                  }}
                  style={modalCancelButtonStyle}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRevokeConfirm}
                  disabled={!revokeTarget?.id}
                  style={{
                    ...modalConfirmButtonStyle,
                    background: "#b91c1c",
                    opacity: revokeTarget?.id ? 1 : 0.6
                  }}
                >
                  <FaTimes /> Revoke Key
                </button>
              </div>
            </div>
          </div>
        )}

        {/* One-time Plaintext Key Reveal Modal */}
        {revealedKey && (
          <div style={modalOverlayStyle}>
            <div style={modalContentStyle}>
              <div style={modalHeaderStyle}>
                <FaShieldAlt color="#6366f1" size={20} />
                <h3 style={modalTitleStyle}>Enrollment Key Ready</h3>
              </div>
              <div style={modalBodyStyle}>
                <div style={{ ...infoBoxStyle, background: "#fffbeb", borderColor: "#fde68a", color: "#92400e" }}>
                  <FaExclamationTriangle color="#b45309" />
                  <span style={infoTextStyle}>
                    This key will <strong>not be shown again</strong>. Copy it before closing.
                  </span>
                </div>
                <div style={inputGroupStyle}>
                  <label style={modalLabelStyle}>Enrollment Key</label>
                  <div style={{ display: "flex", alignItems: "stretch", gap: 8 }}>
                    <div
                      style={{
                        ...modalInputStyle,
                        flex: 1,
                        fontFamily: "monospace",
                        background: "#f8fafc",
                        wordBreak: "break-all"
                      }}
                    >
                      {revealedKey.enrollmentKey}
                    </div>
                    <button
                      type="button"
                      onClick={copyRevealedKey}
                      style={{
                        ...modalConfirmButtonStyle,
                        background: revealCopied ? "#16a34a" : "#6366f1"
                      }}
                      aria-label={revealCopied ? "Enrollment key copied" : "Copy enrollment key to clipboard"}
                    >
                      {revealCopied ? <FaCheck /> : <FaCopy />} {revealCopied ? "Copied" : "Copy"}
                    </button>
                  </div>
                </div>
              </div>
              <div style={modalFooterStyle}>
                <button onClick={closeRevealModal} style={modalCancelButtonStyle}>
                  {revealCopied ? "I've copied the key — close" : "Close"}
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{`
          .spin { animation: fa-spin 2s infinite linear; }
          @keyframes fa-spin { from { transform: rotate(0deg); } to { transform: rotate(359deg); } }
        `}</style>
      </div>
    </div>
  );
};

export default EnrollmentKeys;

/* ------------------ STYLES (mirrors ApiKeys.js) ------------------ */
const dropdownContainerStyle = { position: "relative", width: "100%" };
const dropdownTriggerStyle = {
  width: "100%",
  padding: "10px 14px",
  borderRadius: 10,
  border: "1.5px solid #e2e8f0",
  fontSize: 14,
  background: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  fontWeight: 500,
  color: "#1e293b",
  transition: "all 0.2s ease",
  outline: "none"
};
const dropdownTriggerTextStyle = { flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const dropdownMenuStyle = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  background: "#fff",
  border: "1.5px solid #e2e8f0",
  borderRadius: 10,
  marginTop: 4,
  boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
  zIndex: 1000,
  overflow: "hidden"
};
const searchBoxContainerStyle = { position: "relative", padding: "8px 12px", borderBottom: "1px solid #f1f5f9", background: "#fcfdfe" };
const searchIconStyle = { position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 12, pointerEvents: "none" };
const dropdownSearchInputStyle = {
  width: "100%",
  padding: "8px 12px 8px 28px",
  borderRadius: 6,
  border: "1px solid #e2e8f0",
  fontSize: 13,
  outline: "none",
  fontFamily: "inherit",
  background: "#fff"
};
const dropdownListStyle = { maxHeight: "200px", overflowY: "auto", padding: "4px" };
const dropdownOptionStyle = {
  width: "100%",
  padding: "10px 12px",
  border: "none",
  textAlign: "left",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderRadius: 6,
  margin: "2px 0",
  transition: "all 0.2s ease",
  fontSize: 13
};
const optionTextStyle = { flex: 1 };
const optionNameStyle = { fontWeight: 600, color: "inherit" };
const optionIdStyle = { fontSize: "11px", opacity: 0.6, marginTop: 2 };
const emptyOptionStyle = { padding: "12px", textAlign: "center", color: "#94a3b8", fontSize: 13 };
const searchableDropdownStyles = {
  container: dropdownContainerStyle,
  trigger: dropdownTriggerStyle,
  openTrigger: { border: "1.5px solid #6366f1" },
  triggerText: dropdownTriggerTextStyle,
  indicator: { color: "#94a3b8" },
  menu: dropdownMenuStyle,
  searchContainer: searchBoxContainerStyle,
  searchIcon: searchIconStyle,
  searchInput: dropdownSearchInputStyle,
  list: dropdownListStyle,
  option: dropdownOptionStyle,
  optionText: optionTextStyle,
  optionName: optionNameStyle,
  optionId: optionIdStyle,
  emptyOption: emptyOptionStyle
};

const wrapperStyle = { width: "100%", minHeight: "100vh", background: "#f8fafc", paddingBottom: 60, fontFamily: "Inter, sans-serif" };
const containerStyle = { maxWidth: 1200, margin: "0 auto", padding: "24px" };

const headerStyle = { marginBottom: 32 };
const headerContentStyle = { display: "flex", alignItems: "center", gap: 16 };
const headerBadgeStyle = {
  width: 48,
  height: 48,
  borderRadius: 12,
  background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff"
};

const titleStyle = { fontSize: 24, fontWeight: 800, margin: 0, color: "#0f172a" };
const subtitleStyle = { fontSize: 14, color: "#64748b", margin: "4px 0 0 0" };

const filtersCardStyle = { background: "#fff", borderRadius: 16, padding: 24, marginBottom: 24, border: "1px solid #e2e8f0" };
const cardHeaderStyle = { display: "flex", alignItems: "center", gap: 10, marginBottom: 20, borderBottom: "1px solid #f1f5f9", paddingBottom: 12 };
const cardTitleStyle = { fontSize: 15, fontWeight: 700 };
const filtersGridStyleSingleColumn = { display: "grid", gridTemplateColumns: "1fr", gap: 16, maxWidth: 500 };
const inputGroupStyle = { display: "flex", flexDirection: "column", gap: 6 };
const labelStyle = { fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" };

const actionCardStyle = { background: "#fff", borderRadius: 16, padding: 24, marginBottom: 24, border: "1px solid #e2e8f0" };
const actionButtonsRow = { display: "flex", gap: 16 };
const primaryButtonStyle = {
  background: "#6366f1",
  color: "#fff",
  border: "none",
  padding: "11px 20px",
  borderRadius: 10,
  cursor: "pointer",
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  gap: 8,
  transition: "all 0.2s ease"
};
const secondaryButtonStyle = {
  background: "#f1f5f9",
  color: "#64748b",
  border: "1px solid #e2e8f0",
  padding: "11px 20px",
  borderRadius: 10,
  cursor: "pointer",
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  gap: 8,
  transition: "all 0.2s ease"
};
const secondaryActionButtonStyle = {
  background: "#eef2ff",
  color: "#4338ca",
  border: "1px solid #c7d2fe",
  padding: "8px 12px",
  borderRadius: 10,
  fontWeight: 800
};
const dangerButtonStyle = { background: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca", padding: "8px 12px", borderRadius: 10, fontWeight: 800 };
const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999
};
const modalContentStyle = { background: "#fff", borderRadius: 16, width: "90%", maxWidth: 520, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" };
const modalHeaderStyle = { display: "flex", alignItems: "center", gap: 12, padding: "24px 24px 16px 24px", borderBottom: "1px solid #f1f5f9" };
const modalTitleStyle = { fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 };
const modalBodyStyle = { padding: 24 };
const infoBoxStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "12px 16px",
  background: "#f5f3ff",
  border: "1px solid #e9d5ff",
  borderRadius: 8,
  marginBottom: 20,
  fontSize: 13,
  color: "#5b21b6"
};
const infoTextStyle = { flex: 1, fontWeight: 500 };
const modalLabelStyle = { fontSize: 13, fontWeight: 700, color: "#475569", marginBottom: 8 };
const modalInputStyle = { width: "100%", padding: "12px 14px", borderRadius: 10, border: "1.5px solid #e2e8f0", fontSize: 14, outline: "none", fontFamily: "inherit" };
const modalFooterStyle = { display: "flex", justifyContent: "flex-end", gap: 12, padding: "16px 24px 24px 24px", borderTop: "1px solid #f1f5f9" };
const modalCancelButtonStyle = { padding: "10px 20px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#fff", color: "#64748b", fontWeight: 700 };
const modalConfirmButtonStyle = { padding: "10px 20px", borderRadius: 8, border: "none", background: "#6366f1", color: "#fff", fontWeight: 800, display: "flex", alignItems: "center", gap: 8 };

const existingKeyCardStyle = { background: "#fff", borderRadius: 16, padding: 24, marginBottom: 24, border: "1px solid #e2e8f0" };
const loadingTextStyle = { fontSize: 12, color: "#64748b", fontWeight: 700, display: "flex", alignItems: "center", gap: 8 };
const emptyStateStyle = { padding: "12px 4px", color: "#64748b", fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 8 };

const statusBadgeStyle = { padding: "4px 10px", borderRadius: 999, fontSize: 12, fontWeight: 900, border: "1px solid rgba(0,0,0,0.06)" };

const tableStyle = { display: "flex", flexDirection: "column", gap: 6 };
const tableRowStyle = { display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 0.7fr 1fr", gap: 12, alignItems: "center", padding: "10px 6px", borderBottom: "1px solid #f1f5f9" };
const originRowStyle = { display: "grid", gridTemplateColumns: "1.5fr 1fr 0.6fr", gap: 12, alignItems: "center", padding: "10px 6px", borderBottom: "1px solid #f1f5f9" };
const tableHeaderRowStyle = { fontSize: 12, fontWeight: 900, color: "#64748b", textTransform: "uppercase" };
const tableCellStyle = { fontSize: 13, fontWeight: 700, color: "#1e293b" };
